import { autorun, makeAutoObservable, runInAction } from "mobx";
import { CompanyRole, CompanyroleId } from "@/types/CompanyInfo";
import { getFullName, isBoardMember, isDeputyBoardMember, isObserver } from "@/utils/Roles";
import {
  CleanupFunction,
  DbotEvent,
  DbotEventDispatcher,
  DecisionItem,
  DecisionTextModel,
  DetailsPanel,
  DocumentContext,
  DocumentItem,
  isLanguageChanged,
  mapDecisionTextModel,
  mapDocumentLanguageToLocales,
  mapLocalesToDocumentLanguage,
  mapLocalizedDecisionTextsToModel,
  ProjectDocumentStatus,
  updateBoardMeetingParticipantsEvent,
  UpdateBoardMeetingRecipientItem,
  UpdateBoardMeetingRecipientsResultEvent,
  WithInitialization,
} from "@/dbot/common/commonDomain";
import { toLocalDateISOString } from "@/utils/Dates";
import { identity } from "lodash";
import {
  boardMeetingParticipantsLabel,
  ExtraParticipant,
  getSelectedBoardRoleIds,
  getSortedBoard,
  handleRemoveCustomBoardMeetingPanel,
  mapRoleToProjectRole,
  prepProjectCompanyRoleModel,
  ProjectCompanyRole,
} from "@/dbot/common/commonBoardMeetingDomain";
import { newUuid } from "@/utils/uuid";
import { DocumentLanguage, isOk, LocalizedText } from "@/common/common";
import { formatDateAndTime } from "@/i18n/dateFormat";
import {
  openNotifyNoticeChangeDialogAction,
  openSendNoticeDialogAction,
} from "@/dbot/summary/summaryDomain";
import { Language } from "@/i18n/languages";
import { DocumentType } from "@/common/documents";
import { MeetingForm, MeetingPlace } from "@/meetings/meetingsDomain";
import {
  createEmailField,
  documentLanguageFieldModel,
  LocalDateFieldModel,
  OptionFieldModel,
  RadioFieldModel,
  StringFieldModel,
  TimeFieldModel,
} from "@/common/commonForms";
import { LocalizedTextEditorModel } from "@/text/localizedTextEditorDomain";
import { RoleType } from "@/types/enums/RoleType";

export enum BoardMeetingForm {
  PHYSICAL = "physical_meeting",
  ELECTRONIC = "electronic",
  HYBRID = "hybrid",
  CIRCULATION = "circulation",
}

export const mapBoardMeetingFormToMeetingForm = (f: BoardMeetingForm): MeetingForm => {
  switch (f) {
    case BoardMeetingForm.PHYSICAL:
      return MeetingForm.PHYSICAL;
    case BoardMeetingForm.ELECTRONIC:
      return MeetingForm.ELECTRONIC;
    case BoardMeetingForm.HYBRID:
      return MeetingForm.HYBRID;
    case BoardMeetingForm.CIRCULATION:
      return MeetingForm.CIRCULATION;
  }
};

export enum NoticeBoardMeetingLinkOption {
  linkGiven = "linkGiven",
  linkGivenLater = "linkGivenLater",
}

export type NoticeOfBoardMeeting = {
  type: DocumentType.notice_board_meeting;
  dateNoticeBoardMeeting?: string;
  documentLanguage: Language[];
  meetingForm: BoardMeetingForm;
  meetingPlace: MeetingPlace;
  meetingLinkOption: NoticeBoardMeetingLinkOption;
  inlineAddress: string;
  inlineMeetingLink: string;
  board: ProjectCompanyRole[];
  participants: CompanyroleId[];
  extraParticipants: ExtraParticipant[];
};

export const noticeOfBoardMeeting = (
  data: Partial<NoticeOfBoardMeeting> = {},
): NoticeOfBoardMeeting => ({
  type: DocumentType.notice_board_meeting,
  dateNoticeBoardMeeting: data.dateNoticeBoardMeeting,
  documentLanguage: data.documentLanguage ?? [Language.NO],
  meetingForm: data.meetingForm ?? BoardMeetingForm.HYBRID,
  meetingPlace: data.meetingPlace ?? MeetingPlace.companysOffice,
  meetingLinkOption: data.meetingLinkOption ?? NoticeBoardMeetingLinkOption.linkGivenLater,
  inlineAddress: data.inlineAddress ?? "",
  inlineMeetingLink: data.inlineMeetingLink ?? "",
  board: data.board ?? [],
  participants: data.participants ?? [],
  extraParticipants: data.extraParticipants ?? [],
});

export class NoticeOfBoardMeetingForm implements WithInitialization {
  readonly type = DocumentType.notice_board_meeting;
  context: DocumentContext<NoticeOfBoardMeeting>;
  dispatch: DbotEventDispatcher;
  meetingLinkOption: RadioFieldModel<NoticeBoardMeetingLinkOption>;
  inlineMeetingLink: StringFieldModel;
  dateNoticeBoardMeeting: LocalDateFieldModel;
  documentLanguage: RadioFieldModel<DocumentLanguage>;
  decisionTextModels: DecisionTextModel[];
  board: ProjectCompanyRole[];
  selectedBoardMembers: OptionFieldModel<ProjectCompanyRole>;
  selectedCeos: OptionFieldModel<ProjectCompanyRole>;
  selectedDeputyMembers: OptionFieldModel<ProjectCompanyRole>;
  selectedObservers: OptionFieldModel<ProjectCompanyRole>;
  extraParticipants: ExtraParticipant[];
  extraParticipantFormModel?: { nameField: StringFieldModel; emailField: StringFieldModel };

  constructor(context: DocumentContext<NoticeOfBoardMeeting>, dispatch: DbotEventDispatcher) {
    makeAutoObservable(this, {}, { autoBind: true });

    this.context = context;
    this.dispatch = dispatch;
    const data = context.documentData;

    this.dateNoticeBoardMeeting = new LocalDateFieldModel({
      value: data?.dateNoticeBoardMeeting ?? toLocalDateISOString(Date.now()),
    });
    this.documentLanguage = documentLanguageFieldModel({
      orgNo: context.orgNo,
      value: mapLocalesToDocumentLanguage(data?.documentLanguage),
    });
    this.meetingLinkOption = createMeetingLinkOptionField(
      data?.meetingLinkOption ?? NoticeBoardMeetingLinkOption.linkGivenLater,
    );
    this.inlineMeetingLink = new StringFieldModel({ value: data?.inlineMeetingLink ?? "" });

    this.board = getSortedBoard(context);
    this.extraParticipants = data?.extraParticipants ?? [];

    this.selectedBoardMembers = prepProjectCompanyRoleModel({
      options: this.boardMembers,
      selectedIds: data?.participants,
      defaultValue: this.boardMembers,
    });

    this.selectedCeos = prepProjectCompanyRoleModel({
      options: this.availableCeos,
      selectedIds: data?.participants,
      defaultValue: this.availableCeos,
    });

    this.selectedDeputyMembers = prepProjectCompanyRoleModel({
      options: this.deputyMembers,
      selectedIds: data?.participants,
      defaultValue: [],
    });

    this.selectedObservers = prepProjectCompanyRoleModel({
      options: this.observers,
      selectedIds: data?.participants,
      defaultValue: [],
    });

    this.decisionTextModels = mapLocalizedDecisionTextsToModel(
      context,
      dispatch,
      DocumentType.notice_board_meeting,
      this.documentLanguage.value,
    );
  }

  init(): CleanupFunction {
    const cleanupFunctions = [autorun(() => this.handleLanguageChanges())];

    return () => {
      cleanupFunctions.forEach((f) => f());
    };
  }
  get locale(): Language {
    return this.context.locale;
  }

  get newRecipients(): ProjectCompanyRole[] {
    const participants = this.selectedBoardMembers.selected
      .concat(this.selectedCeos.selected)
      .concat(this.selectedDeputyMembers.selected)
      .concat(this.selectedObservers.selected);

    return participants.filter((r) => !this.existingRecipients.includes(String(r.companyroleId)));
  }

  get newExtraParticipants(): ExtraParticipant[] {
    return this.extraParticipants.filter((p) => !this.existingRecipients.includes(p.email));
  }

  get numberOfNewRecipients(): number {
    return this.newRecipients.length + this.newExtraParticipants.length;
  }

  get roles(): ProjectCompanyRole[] {
    return this.context.initialState.roles.map(mapRoleToProjectRole);
  }

  get meetingForm(): RadioFieldModel<BoardMeetingForm> {
    return this.context.boardMeetingFields.boardMeetingForm;
  }

  get meetingPlace(): RadioFieldModel<MeetingPlace> {
    return this.context.boardMeetingFields.meetingPlace;
  }

  get inlineAddress(): StringFieldModel {
    return this.context.boardMeetingFields.inlineAddress;
  }

  get boardMeetingNumber(): StringFieldModel {
    return this.context.boardMeetingFields.boardMeetingNumber;
  }

  get meetingStartDate(): LocalDateFieldModel {
    return this.context.boardMeetingFields.meetingStartDate;
  }

  get meetingStartTime(): TimeFieldModel {
    return this.context.boardMeetingFields.meetingStartTime;
  }

  get meetingEndTime(): TimeFieldModel {
    return this.context.boardMeetingFields.meetingEndTime;
  }

  get documentId(): string {
    const noticeDocument = this.context.currentDocuments.find(
      (d) => d.type === DocumentType.notice_board_meeting,
    );
    return noticeDocument?.documentId ?? "n/a";
  }

  get documentItem(): DocumentItem {
    return this.context.currentDocuments.find((d) => d.documentId === this.documentId)!;
  }

  get isNoticeSent() {
    return this.documentStatus?.sentAt !== undefined;
  }

  get existingRecipients() {
    return this.documentStatus?.viewerRecipientIds ?? [];
  }

  get documentStatus(): ProjectDocumentStatus | undefined {
    return this.context.documentStatuses.find((s) => s.documentId === this.documentId);
  }

  get availableCeos() {
    return this.roles.filter((r) => r.role === RoleType.CEO);
  }

  get boardMembers(): ProjectCompanyRole[] {
    return this.roles.filter(isBoardMember);
  }

  get deputyMembers(): ProjectCompanyRole[] {
    return this.roles.filter(isDeputyBoardMember);
  }

  get observers(): ProjectCompanyRole[] {
    return this.roles.filter(isObserver);
  }

  get hasCeos(): boolean {
    return this.availableCeos.length > 0;
  }

  get hasDeputyBoardMembers(): boolean {
    return this.deputyMembers.length > 0;
  }

  get hasObservers(): boolean {
    return this.observers.length > 0;
  }

  get currentDecisions(): DecisionItem[] {
    return this.context.currentDecisions;
  }

  get showAddress(): boolean {
    return (
      this.meetingForm.value === BoardMeetingForm.HYBRID ||
      this.meetingForm.value === BoardMeetingForm.PHYSICAL
    );
  }

  get showInlineAddress(): boolean {
    return this.showAddress && this.meetingPlace.value === MeetingPlace.inlineAddress;
  }

  get showElectronicLink(): boolean {
    return (
      this.meetingForm.value === BoardMeetingForm.HYBRID ||
      this.meetingForm.value === BoardMeetingForm.ELECTRONIC
    );
  }

  get showElectronicLinkInput(): boolean {
    return (
      this.showElectronicLink &&
      this.meetingLinkOption.value === NoticeBoardMeetingLinkOption.linkGiven
    );
  }

  get isLocked(): boolean {
    return this.context.isLocked;
  }

  handleEvent(event: DbotEvent) {
    switch (event.type) {
      case "dbot/decision-added":
        this.initDecisionTextModel(event.decisionId);
        break;
      case "dbot/update-board-meeting-recipients-result":
        this.updateBoardMeetingNoticeRecipients(event);
        break;
      case "dbot/send-document-for-viewing-result":
        if (this.documentId === event.documentId && event.isResend === false) {
          this.dispatch(
            updateBoardMeetingParticipantsEvent(
              getSelectedBoardRoleIds(this),
              this.extraParticipants.slice(),
            ),
          );
        }
        break;
      case "dbot/remove-custom-panel":
        handleRemoveCustomBoardMeetingPanel(this.context, event);
        break;
    }
  }

  updateBoardMeetingNoticeRecipients(event: UpdateBoardMeetingRecipientsResultEvent) {
    if (isOk(event.result)) {
      this.board = updateParticipants(this.board, event.result.data);
      this.extraParticipants = updateParticipants(this.extraParticipants, event.result.data);
    }
  }

  addRoleAsExtraParticipant(r: CompanyRole) {
    this.extraParticipants.push({
      id: String(r.companyroleId),
      name: getFullName(r),
      email: r.email ?? "",
    });
  }

  addExtraParticipant() {
    this.extraParticipantFormModel = {
      nameField: new StringFieldModel({ required: true }),
      emailField: createEmailField({ required: true }, () =>
        this.extraParticipants.map((p) => p.email),
      ),
    };
  }

  cancelAddExtraParticipant() {
    this.extraParticipantFormModel = undefined;
  }

  submitExtraParticipant() {
    if (!this.extraParticipantFormModel) {
      return;
    }
    const { nameField, emailField } = this.extraParticipantFormModel;
    nameField.validate();
    emailField.validate();
    if (nameField.isValid && emailField.isValid) {
      this.extraParticipants.push({
        id: newUuid(),
        name: nameField.validatedValue,
        email: emailField.validatedValue!,
      });
      nameField.clear();
      emailField.clear();
      nameField.focus();
    }
  }

  removeExtraParticipant(participant: ExtraParticipant) {
    this.extraParticipants.splice(this.extraParticipants.indexOf(participant), 1);
  }

  get openSendNoticeDialogAction() {
    return openSendNoticeDialogAction({
      document: this.documentItem,
      isSendingAvailable: !this.isNoticeSent || this.numberOfNewRecipients > 0,
      isBatchProject: this.context.isBatchProject,
      isSendToNewParticipants: this.isNoticeSent,
      newRecipients: this.isNoticeSent ? this.numberOfNewRecipients : undefined,
      authMethod: this.documentStatus?.authMethod,
      meetingPassword: this.documentStatus?.meetingPassword,
    });
  }

  get openNotifyNoticeChangeDialogAction() {
    return openNotifyNoticeChangeDialogAction({
      document: this.documentItem,
      sendingTime: this.documentStatus?.sentAt,
    });
  }

  initDecisionTextModel(decisionId: string) {
    const decisionTextModel = this.decisionTextModels.find((d) => d.decisionId === decisionId);
    if (decisionTextModel === undefined) {
      const decision = this.context.currentDecisions.find((d) => d.decisionId === decisionId);
      const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
      if (decision) {
        const model = mapDecisionTextModel(
          this.dispatch,
          decision,
          DocumentType.notice_board_meeting,
          this.context,
          documentLanguages,
        );
        this.decisionTextModels.push(model);
      }
    }
  }

  get noticeDescription(): LocalizedText[] {
    return this.context.jsonData.boardMeeting?.noticeDescription ?? [];
  }

  get noticeDescriptionModel(): LocalizedTextEditorModel {
    return this.context.boardMeetingFields.noticeDescription;
  }

  private handleLanguageChanges() {
    const documentLanguages = mapDocumentLanguageToLocales(this.documentLanguage.value);
    if (isLanguageChanged(documentLanguages, this.noticeDescriptionModel.textModels)) {
      runInAction(() => {
        this.noticeDescriptionModel.onLanguageChange(documentLanguages, this.noticeDescription);
        this.decisionTextModels = mapLocalizedDecisionTextsToModel(
          this.context,
          this.dispatch,
          DocumentType.notice_board_meeting,
          this.documentLanguage.value,
        );
      });
    }
  }
}

function updateParticipants(
  current: ProjectCompanyRole[],
  updates: UpdateBoardMeetingRecipientItem[],
): ProjectCompanyRole[];
function updateParticipants(
  current: ExtraParticipant[],
  updates: UpdateBoardMeetingRecipientItem[],
): ExtraParticipant[];
function updateParticipants<T extends ProjectCompanyRole | ExtraParticipant>(
  currentOptions: T[],
  updates: UpdateBoardMeetingRecipientItem[],
): T[] {
  return currentOptions.map((option) => {
    const id = "companyroleId" in option ? option.companyroleId : option.id;
    const update = updates.find((u) => u.id === id);
    if (update) {
      return {
        ...option,
        email: update.email,
      };
    }
    return option;
  });
}

export function createMeetingLinkOptionField(
  linkOption: NoticeBoardMeetingLinkOption,
): RadioFieldModel<NoticeBoardMeetingLinkOption> {
  return new RadioFieldModel<NoticeBoardMeetingLinkOption>({
    value: linkOption,
    options: Object.values(NoticeBoardMeetingLinkOption),
    idFn: identity,
    labelFn: (v) => {
      switch (v) {
        case NoticeBoardMeetingLinkOption.linkGiven:
          return { key: "documents.meetingForm.electronicParticipationLinkGiven" };
        case NoticeBoardMeetingLinkOption.linkGivenLater:
          return { key: "documents.meetingForm.electronicParticipationLinkGivenLater" };
      }
    },
  });
}

export function applyForm(form: NoticeOfBoardMeetingForm): NoticeOfBoardMeeting {
  return {
    type: DocumentType.notice_board_meeting,
    dateNoticeBoardMeeting: form.dateNoticeBoardMeeting.toISOString(),
    documentLanguage: mapDocumentLanguageToLocales(form.documentLanguage.value),
    meetingForm: form.meetingForm.value,
    meetingPlace: form.meetingPlace.value,
    meetingLinkOption: form.meetingLinkOption.value,
    inlineAddress: form.inlineAddress.validatedValue,
    inlineMeetingLink: form.inlineMeetingLink.validatedValue,
    board: form.board.slice(),
    participants: getSelectedBoardRoleIds(form),
    extraParticipants: form.extraParticipants.slice(),
  };
}

export enum NoticeOfBoardMeetingCustomPanel {
  meetingDetails = "meetingDetails",
  recipients = "recipients",
}

export function getNoticeOfBoardMeetingCustomPanels(
  form: NoticeOfBoardMeetingForm,
): DetailsPanel[] {
  return [
    {
      type: "custom",
      id: NoticeOfBoardMeetingCustomPanel.meetingDetails,
      documentId: form.documentId,
      icon: "calendar",
      heading: { key: "dbot.meeting-details" },
      label: {
        translated: formatDateAndTime(
          form.locale,
          form.meetingStartDate.validatedValue,
          form.meetingStartTime.validatedValue,
        ),
      },
    },
    {
      type: "custom",
      id: NoticeOfBoardMeetingCustomPanel.recipients,
      documentId: form.documentId,
      icon: "users",
      heading: { key: "dbot.recipients" },
      label: boardMeetingParticipantsLabel({
        board: form.boardMembers,
        participants: form.selectedBoardMembers.selected
          .concat(form.selectedDeputyMembers.selected)
          .map((m) => m.companyroleId),
        extraParticipants: form.extraParticipants,
      }),
    },
  ];
}
